import type {
  AnsweredConsentSignals,
  ConsentSignals,
} from '@/lib/consent/consent-signals';

import type { AnalyticsAdapter } from './adapters/types';
import type { ConversionEventPayload } from './conversion-events';
import type {
  CaptureOptions,
  OperationalEventPayload,
  RoutingContext,
  ScreenEventPayload,
  TrackEventPayload,
  UIEventPayload,
} from './types';

import {
  publishAnalyticsConsent,
  publishConsentUpdate,
} from './adapters/consent-mode-bootstrap';
import { conversionEventName } from './conversion-events';
import {
  isCredentialBearingPath,
  redactCredentialBearingUrl,
} from './credential-bearing-path';
import { route } from './internal/route';

/**
 * The routing context an event falls back on when the real one cannot be
 * derived. `unknown` is a real routing outcome rather than a defect — an
 * anonymous visitor on a route that names neither side has no side — so an
 * event that lands here is still delivered, and the edge treats it as
 * unattributed.
 */
const UNATTRIBUTED_CONTEXT: RoutingContext = { side: 'unknown' };

export class AnalyticsClient {
  constructor(
    private readonly adapters: ReadonlyArray<AnalyticsAdapter>,
    private readonly getContext: () => RoutingContext,
  ) {}

  /**
   * Emits a pageview — except on a path whose URL is itself a credential,
   * where the event is dropped rather than routed.
   *
   * A pageview's payload is the URL, so there is no version of this event
   * that a reset or verification token can safely travel in: GA4 sends it
   * as `page_location`, and the GTM feed's `rio.page` entry lands in a
   * global array every tag in the container can read. Dropping it costs the
   * pageview count of five routes.
   *
   * The guard is here rather than at the call site because both the
   * pathname and the full URL are already resolved on the event by the
   * time it reaches this method, and every destination is downstream of it.
   *
   * The pageview that follows a token route is a different problem, and is
   * not dropped: it reports the token URL as its referrer. See
   * {@link redactScreenReferrer}.
   */
  screen(payload: ScreenEventPayload, options?: CaptureOptions) {
    const attributes = this.withScreenDefaults(payload.attributes);
    if (carriesCredentialBearingUrl(attributes)) return;
    this.dispatch(options, 'Analytics screen error', (adapter) =>
      adapter.page(payload.name, attributes, options),
    );
  }

  ui(payload: UIEventPayload, options?: CaptureOptions) {
    const { action, actionSubject, attributes, ...rest } = payload;
    const properties = withEventId({ ...rest, ...attributes });
    this.dispatch(options, 'Analytics ui error', (adapter) =>
      adapter.track(`ui.${actionSubject}.${action}`, properties, options),
    );
  }

  track(payload: TrackEventPayload, options?: CaptureOptions) {
    const { action, actionSubject, attributes, ...rest } = payload;
    const properties = withEventId({ ...rest, ...attributes });
    this.dispatch(options, 'Analytics track error', (adapter) =>
      adapter.track(`track.${actionSubject}.${action}`, properties, options),
    );
  }

  operational(payload: OperationalEventPayload, options?: CaptureOptions) {
    const { action, actionSubject, attributes, ...rest } = payload;
    const properties = withEventId({ ...rest, ...attributes });
    this.dispatch(options, 'Analytics operational error', (adapter) =>
      adapter.track(
        `operational.${actionSubject}.${action}`,
        properties,
        options,
      ),
    );
  }

  /**
   * Emits a conversion event from the closed PX-268 catalog as
   * `rio.{name}` — the funnel the ad platforms optimise against.
   *
   * This is a fifth call-site method rather than a `track` convention
   * because the concept genuinely differs from the other four. `track`,
   * `ui`, `screen` and `operational` are our own product taxonomy: the
   * names are free-form, and their only consumers are our dashboards and
   * warehouse. A conversion event is a contract with systems outside this
   * repository — a server-side CAPI module maps these exact strings to Meta
   * and Reddit conversions — so the name comes from a catalog, the
   * marketplace `side` is always present, and two of them carry an id that a
   * foreign system joins on. Encoding that in the `track.{subject}.{action}`
   * taxonomy would bury a cross-system contract inside a naming convention
   * we otherwise treat as ours to change.
   *
   * `side` is stamped from the routing context the event is dispatched on,
   * not asked for at the call site: it is the same derivation routing
   * performs, and a hand-passed value would be one more thing to drift. A
   * call site that knows better can override it with `payload.side`, and
   * that override survives an unresolvable context. Any `side` in
   * `attributes` is ignored in favour of the resolved one.
   */
  conversion(payload: ConversionEventPayload, options?: CaptureOptions) {
    const { name, side, eventId, attributes } = payload;
    this.dispatch(options, 'Analytics conversion error', (adapter, context) => {
      const properties: Record<string, unknown> = {
        ...attributes,
        side: side ?? context.side,
      };
      // Only the two shared-id events carry one, and their payload type
      // makes it required — so its presence here needs no further gating.
      if (eventId) properties.eventId = eventId;
      adapter.track(conversionEventName(name), properties, options);
    });
  }

  captureException(
    error: unknown,
    additionalProperties?: Record<string, unknown>,
    options?: CaptureOptions,
  ) {
    // Exceptions always route to PostHog, regardless of side. Error tracking is
    // deliberately exempt from the participant cost-control exclusion that
    // applies to product analytics (track/page/identify stay side-gated): under
    // `identified_only`, participant exceptions are cheap anonymous events that
    // never create person profiles, and we want participant crashes visible in
    // the PostHog error-tracking project. A caller can still opt a specific
    // exception out with `{ destinations: { posthog: false } }`.
    const withPostHog: CaptureOptions = {
      ...options,
      destinations: { posthog: true, ...options?.destinations },
    };
    this.dispatch(withPostHog, 'Analytics captureException error', (adapter) =>
      adapter.trackException(error, additionalProperties, withPostHog),
    );
  }

  identify(
    userId: string,
    traits?: Record<string, unknown>,
    options?: CaptureOptions,
  ) {
    // Routed (not fanned out) — identify counts as billable activity on
    // PostHog. Participants are excluded from PostHog by the routing rule,
    // so identifying them there would partially defeat the cost-control
    // goal (events excluded, but the user still tracked).
    //
    // Accepts the same `destinations` override map as other routed methods
    // (Segment SDK convention), so callers can force PostHog for a
    // specific identify when there's a deliberate reason — e.g. a key
    // participant-side conversion the team wants in PostHog despite cost.
    this.dispatch(options, 'Analytics identify error', (adapter) =>
      adapter.identify(userId, traits),
    );
  }

  reset() {
    this.fanout('Analytics reset error', (adapter) => adapter.reset());
  }

  /**
   * Publishes the resolved Consent Mode signal set, then hands it to every
   * adapter. This is the consent entry point; `optIn`/`optOut` remain for the
   * narrow analytics-only cases.
   *
   * Consent Mode is published here rather than by an adapter because it is
   * page state that every Google tag reads, not a destination: an adapter
   * that owned it would stop publishing the moment its own env var was
   * unset. This is the one path a resolved consent decision travels
   * (`AnalyticsProvider`'s effect is its only production caller), so
   * publishing here is what makes it impossible to reach the destinations
   * without the tags hearing about it too.
   */
  setConsent(
    signals: ConsentSignals,
    answeredSignals?: AnsweredConsentSignals,
  ) {
    this.guard('Analytics setConsent error', () =>
      publishConsentUpdate(signals),
    );
    this.fanout('Analytics setConsent error', (adapter) =>
      adapter.setConsent(signals, answeredSignals),
    );
  }

  optIn() {
    this.guard('Analytics optIn error', () =>
      publishAnalyticsConsent('granted'),
    );
    this.fanout('Analytics optIn error', (adapter) => adapter.optIn());
  }

  optOut() {
    this.guard('Analytics optOut error', () =>
      publishAnalyticsConsent('denied'),
    );
    this.fanout('Analytics optOut error', (adapter) => adapter.optOut());
  }

  toggleSessionRecording(toState: 'on' | 'off', options?: CaptureOptions) {
    // Routed (not fanned out) — session replay is billed separately on
    // PostHog, so participant-side calls must not silently start a
    // recording. Default routing keeps participants out; callers that
    // genuinely want a participant session recording (rare) can pass
    // `{ destinations: { posthog: true } }`.
    this.dispatch(
      options,
      'Analytics toggleSessionRecording error',
      (adapter) => adapter.toggleSessionRecording(toState),
    );
  }

  /**
   * Dispatches a capture-style call (track/ui/screen/operational/exception)
   * through the routing function — console adapter always receives, real
   * adapters only when their name is in the routed destination set.
   *
   * The context is resolved once here and handed to `fn`, so an event that
   * stamps a side onto its own properties reports the side it was routed on.
   */
  private dispatch(
    options: CaptureOptions | undefined,
    errorPrefix: string,
    fn: (adapter: AnalyticsAdapter, context: RoutingContext) => void,
  ) {
    const context = this.resolveContext(errorPrefix);
    const destinations = route(options, context);
    for (const adapter of this.adapters) {
      const shouldDispatch =
        adapter.name === 'console' || destinations.includes(adapter.name);
      if (!shouldDispatch) continue;
      try {
        fn(adapter, context);
      } catch (error) {
        console.error(errorPrefix, error);
      }
    }
  }

  /**
   * Fans out a consent/cleanup call to every registered adapter
   * unconditionally — currently `reset`, `setConsent`, `optIn`, `optOut`. These don't
   * generate billable activity and should reach every adapter regardless
   * of current side. (`identify` and `toggleSessionRecording` look like
   * lifecycle calls but ARE routed; see those methods for the why.)
   */
  private fanout(errorPrefix: string, fn: (adapter: AnalyticsAdapter) => void) {
    for (const adapter of this.adapters) {
      this.guard(errorPrefix, () => fn(adapter));
    }
  }

  /**
   * Runs one side effect, logging rather than throwing. Analytics never
   * takes the page down with it: a destination (or the Consent Mode writer)
   * that throws costs its own call and nothing else.
   */
  private guard(errorPrefix: string, fn: () => void) {
    try {
      fn();
    } catch (error) {
      console.error(errorPrefix, error);
    }
  }

  /**
   * The current routing context, or the unattributed one when deriving it
   * throws. A throw means the user/pathname accessors this client was built
   * with are broken, which is not a reason to lose the event: an unattributed
   * event still counts, and dropping a conversion would understate campaign
   * performance.
   */
  private resolveContext(errorPrefix: string): RoutingContext {
    try {
      return this.getContext();
    } catch (error) {
      console.error(errorPrefix, error);
      return UNATTRIBUTED_CONTEXT;
    }
  }

  private withScreenDefaults(
    attributes: Record<string, unknown> | undefined,
  ): Record<string, unknown> {
    const result = withEventId({ ...attributes });

    if (typeof result.path !== 'string') {
      const path = getCurrentPathname();
      if (path) result.path = path;
    }

    if (typeof result.title !== 'string') {
      const title = getDocumentTitle();
      if (title) result.title = title;
    }

    if (typeof result.pageLocation !== 'string') {
      const pageLocation = getCurrentPageLocation();
      if (pageLocation) result.pageLocation = pageLocation;
    }

    redactScreenReferrer(result);

    return result;
  }
}

/**
 * The screen attributes that hold a URL of the page being reported, and so
 * the ones a path-carried token can reach a destination in. `path` is the
 * router's pathname or `window.location.pathname`; `pageLocation` is the
 * absolute URL. Either alone is enough to disqualify the event, since a
 * caller can supply one and let the screen defaults fill the other
 * from the browser.
 */
const SCREEN_URL_ATTRIBUTES = ['path', 'pageLocation'] as const;

/**
 * Truncates a credential-bearing referrer to its origin, in place.
 *
 * Leaving a token route makes that URL the referrer of the next pageview —
 * an event the guard in {@link AnalyticsClient.screen} correctly lets
 * through, since the page being reported is an ordinary one. Truncating to
 * the origin is what a browser does under `Referrer-Policy:
 * strict-origin`, and it is deliberately narrower than dropping the field:
 * a destination that finds no referrer on the event falls back to reading
 * `document.referrer` for itself (the GA adapter does), which is the token
 * URL again. Every other referrer passes through untouched.
 */
function redactScreenReferrer(attributes: Record<string, unknown>) {
  const referrer = attributes.previousPageLocation;
  if (typeof referrer !== 'string') return;
  const redacted = redactCredentialBearingUrl(referrer);
  if (redacted === undefined) delete attributes.previousPageLocation;
  else attributes.previousPageLocation = redacted;
}

function carriesCredentialBearingUrl(
  attributes: Record<string, unknown>,
): boolean {
  return SCREEN_URL_ATTRIBUTES.some((key) => {
    const value = attributes[key];
    return typeof value === 'string' && isCredentialBearingPath(value);
  });
}

/**
 * Stamps a client-generated UUID `eventId` onto an event's properties.
 *
 * Called once per event, BEFORE the adapter fan-out, so every destination
 * (PostHog, first-party collector, GA, GTM) sees the same id for the same
 * event. That shared id is the client/server deduplication key for ad
 * platforms — Meta/Reddit receive the same conversion from the browser
 * pixel and the server-side API, and dedupe on it (PX-270).
 *
 * A caller-supplied `eventId` in attributes wins, so replays and events
 * with a server-known id keep theirs.
 */
function withEventId(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  if (typeof properties.eventId === 'string' && properties.eventId.length > 0) {
    return properties;
  }
  return { ...properties, eventId: createEventId() };
}

function createEventId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  // Fallback for insecure contexts, where `crypto.randomUUID` is not
  // exposed. Not cryptographically strong — a dedup key only needs to be
  // unique, not unguessable.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replaceAll(/[xy]/g, (char) => {
    const random = Math.trunc(Math.random() * 16);
    const value = char === 'x' ? random : (random % 4) + 8;
    return value.toString(16);
  });
}

function getCurrentPathname(): string | undefined {
  if (globalThis.window === undefined) return undefined;
  return globalThis.window.location.pathname;
}

function getCurrentPageLocation(): string | undefined {
  if (globalThis.window === undefined) return undefined;
  return globalThis.window.location.href.split('#')[0];
}

function getDocumentTitle(): string | undefined {
  if (globalThis.document === undefined) return undefined;
  return globalThis.document.title || undefined;
}
